import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, CircleNotch, Sparkle, ArrowUpRight, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function SpeakerUpgradeSuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ phase: "polling", attempts: 0, data: null, error: "" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session_id", attempts: 0, data: null });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    async function poll() {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/me/speaker/featured-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") return setState({ phase: "success", attempts, data, error: "" });
        if (data.status === "expired") return setState({ phase: "expired", attempts, data, error: "" });
        if (attempts >= MAX_ATTEMPTS) return setState({ phase: "timeout", attempts, data, error: "" });
        setState({ phase: "polling", attempts, data, error: "" });
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "error", attempts, data: null, error: e?.response?.data?.detail || e.message });
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="speaker-upgrade-success" className="px-6 md:px-10 lg:px-16 py-16 md:py-24 max-w-2xl mx-auto">
      {state.phase === "polling" && (
        <div className="text-center" data-testid="upgrade-polling">
          <CircleNotch size={36} weight="bold" className="mx-auto text-blue-700 animate-spin mb-6" />
          <div className="overline mb-3">{t("speakerUpgrade.confirming")}</div>
          <h1 className="font-display font-bold text-4xl tracking-tight mb-3">{t("speakerUpgrade.hangTight")}</h1>
          <p className="text-zinc-600">{t("speakerUpgrade.checking", { n: state.attempts, m: MAX_ATTEMPTS })}</p>
        </div>
      )}
      {state.phase === "success" && (
        <div className="text-center" data-testid="upgrade-success">
          <div className="relative inline-flex">
            <CheckCircle size={56} weight="fill" className="text-blue-700 mb-6" />
            <Sparkle size={20} weight="fill" className="absolute -top-1 -right-1 text-blue-400" />
          </div>
          <div className="overline mb-3 text-blue-700">{t("speakerUpgrade.confirmed")}</div>
          <h1 className="font-display font-black text-5xl tracking-[-0.04em] leading-none mb-4">{t("speakerUpgrade.youreFeatured")}</h1>
          <p className="text-zinc-600 leading-relaxed">
            {t("speakerUpgrade.youreFeaturedBody", { name: state.data?.speaker_name || "Your profile" })}
            {state.data?.featured_until && (<><br />{t("speakerUpgrade.activeThrough")} <span className="font-mono text-zinc-950">{new Date(state.data.featured_until).toLocaleDateString()}</span>.</>)}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {state.data?.speaker_id && (
              <Link to={`/speakers/${state.data.speaker_id}`} data-testid="upgrade-view-public" className="btn-primary">{t("speakerUpgrade.viewPublic")} <ArrowUpRight size={14} data-arrow="forward" /></Link>
            )}
            <Link to="/speakers/me" className="btn-secondary">{t("speakerUpgrade.editProfile")}</Link>
          </div>
        </div>
      )}
      {state.phase === "expired" && (
        <div className="text-center">
          <XCircle size={48} className="mx-auto text-zinc-400 mb-6" />
          <h1 className="font-display font-bold text-4xl tracking-tight mb-3">{t("speakerUpgrade.sessionExpired")}</h1>
          <p className="text-zinc-600">{t("speakerUpgrade.sessionExpiredBody")}</p>
          <div className="mt-6"><Link to="/speakers/me" className="btn-primary">{t("speakerUpgrade.tryAgain")}</Link></div>
        </div>
      )}
      {state.phase === "timeout" && (
        <div className="text-center">
          <CircleNotch size={36} className="mx-auto text-zinc-400 mb-6" />
          <h1 className="font-display font-bold text-3xl tracking-tight mb-3">{t("speakerUpgrade.stillConfirming")}</h1>
          <p className="text-zinc-600 max-w-md mx-auto">{t("speakerUpgrade.stillConfirmingBody")}</p>
          <div className="mt-6"><Link to="/speakers/me" className="btn-secondary">{t("speakerUpgrade.backToProfile")}</Link></div>
        </div>
      )}
      {state.phase === "error" && (
        <div className="text-center">
          <XCircle size={48} className="mx-auto text-red-500 mb-6" />
          <h1 className="font-display font-bold text-3xl tracking-tight mb-3">{t("common.somethingWrong")}</h1>
          <p className="text-zinc-600">{state.error}</p>
        </div>
      )}
    </div>
  );
}
